import React, { useState } from "react";
import axios from "axios";
import { baseURL } from "../backend_url";
import Swal from "sweetalert2";

/* ---------- FIELD WRAPPER ---------- */
function Field({ label, required, children, full = false, error }) {
  // inject an error class into the child
  const child = React.cloneElement(children, {
    className: `${children.props.className || ""} ${
      error ? "input-error" : ""
    }`.trim(),
  });

  return (
    <div className={`field ${full ? "full" : ""}`}>
      <label className="label">
        {label}{" "}
        {required ? (
          <span className="req">*</span>
        ) : (
          <span className="opt">(optional)</span>
        )}
      </label>
      {child}
    </div>
  );
}
/* ----------------------------------- */

export default function UpdateForm() {
  const initialState = {
    callType: "Inbound",
    outboundAnswered: "NA",
    customerName: "",
    phoneNumber: "",
    contactEmail: "",
    location: "",
    source: "",
    existingCustomer: "NA",
    sourceDetail: "",
    reservationNo: "",
    verificationDone: "NA",
    isSaleEnquiry: "NA",
    callCategory: "",
    saleStatus: "",
    saleReason: "NA",
    issueResolved: "NA",
    issueNotResolvedReason: "",
    callTransferred: "NA",
    callTransferReason: "",
    prePaid: "NA",
    postPaid: "NA",
    referredToWebsite: "NA",
    feedback: "",
  };

  const [form, setForm] = useState(initialState);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((s) => ({ ...s, [name]: value }));
    // clear error on this field as user types/selects
    setErrors((prev) => (prev[name] ? { ...prev, [name]: false } : prev));
  };

  const markErrors = (missing) => {
    const map = {};
    missing.forEach((f) => (map[f] = true));
    setErrors(map);
  };

  const clearErrors = () => setErrors({});

  const makeTicketId = () =>
    `TI-${Date.now()}-${Math.floor(Math.random() * 900 + 100)}`;

  // Required fields logic (unchanged)
  const getRequiredNow = () => {
    const isSale =
      (form.isSaleEnquiry || "").trim().toLowerCase() === "yes";
    if (isSale) {
      // require a few more when it’s a sale enquiry
      return ["callType", "phoneNumber", "location", "isSaleEnquiry", "reservationNo"];
    }
    // minimal default
    return ["phoneNumber", "reservationNo"];
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const requiredNow = getRequiredNow();
    const missing = requiredNow.filter(
      (f) => !String(form[f] || "").trim()
    );
    if (missing.length > 0) {
      markErrors(missing);
      Swal.fire(
        "Missing information",
        `Please fill: ${missing.join(", ")}`,
        "warning"
      );
      return;
    }
    clearErrors();

    // Normally from logged in user; hard-coded for now
    const createdBy = "test";

    const payload = {
      ...form,
      ticketId: makeTicketId(),
      createdAtUTC: new Date().toISOString(),
      createdBy,
      required: requiredNow.join(","), // server can enforce these
    };

    try {
      setSaving(true);
      const res = await axios.post(`${baseURL}/call_center_interface`, payload, {
        headers: { "Content-Type": "application/json" },
        timeout: 15000,
      });
      Swal.fire(
        "Saved",
        `Ticket: ${res.data.ticketId} • DB _id: ${res.data.insertedId}`,
        "success"
      );
      setForm(initialState);
    } catch (err) {
      console.error(err);
      Swal.fire(
        "Save failed",
        err?.response?.data?.message || "Check API logs.",
        "error"
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="page">
      <div className="card">
        <header
          style={{
            display: "flex",
            alignItems: "center",
            gap: "1rem",
            padding: "1rem 1.2rem",
            background: "linear-gradient(90deg, #f7f7f7, #ffffff)",
            borderBottom: "2px solid #e5e5e5",
          }}
        >
          <div
            style={{
              background: "#2563eb",
              color: "white",
              padding: "0.35rem 0.75rem",
              borderRadius: "8px",
              fontSize: "0.9rem",
              fontWeight: "600",
              display: "flex",
              alignItems: "center",
              boxShadow: "0 2px 4px rgba(0,0,0,0.12)",
            }}
          >
            📞 Call Center Interface
          </div>

          <h1
            style={{
              margin: 0,
              fontSize: "1.6rem",
              fontWeight: "700",
              color: "#1f2937",
            }}
          >
            Customer Interaction Form
          </h1>
        </header>


        <form className="form" onSubmit={handleSubmit}>
          {/* GRID: 1 → 2 → 3 columns */}
          <div className="grid">
            {/* CALL TYPE (required when sale, but we visually treat as important) */}
            <Field label="CALL TYPE" required error={errors.callType}>
              <select
                name="callType"
                value={form.callType}
                onChange={handleChange}
                className="input select56"
              >
                <option>Inbound</option>
                <option>Missed call back</option>
                <option>Outbound</option>
              </select>
            </Field>

            <Field
              label="If Outbound, did the customer answer?"
              error={errors.outboundAnswered}
            >
              <select
                name="outboundAnswered"
                value={form.outboundAnswered}
                onChange={handleChange}
                className="input select56"
              >
                <option>Yes</option>
                <option>No</option>
                <option>NA</option>
              </select>
            </Field>

            {/* BASICS */}
            <Field label="Customer Name" error={errors.customerName}>
              <input
                name="customerName"
                value={form.customerName}
                onChange={handleChange}
                placeholder="Full name"
                className="input"
              />
            </Field>

            <Field label="Phone Number" required error={errors.phoneNumber}>
              <input
                name="phoneNumber"
                value={form.phoneNumber}
                onChange={handleChange}
                placeholder="e.g., +1 555 555 5555"
                className="input"
              />
            </Field>

            <Field label="Email ID" error={errors.contactEmail}>
              <input
                name="contactEmail"
                value={form.contactEmail}
                onChange={handleChange}
                placeholder="name@example.com"
                className="input"
              />
            </Field>

            {/* LOCATION */}
            <Field label="LOCATION" required error={errors.location}>
              <select
                name="location"
                value={form.location}
                onChange={handleChange}
                className="input select56"
              >
                <option value="">Select</option>
                <option>YYZ (Toronto)</option>
                <option>YOW (Ottawa)</option>
                <option>YYC (Calgary)</option>
                <option>YVR (Vancouver)</option>
                <option>Montreal (YUL)</option>
                <option>VGN (Vaughan)</option>
                <option>ORD (Chicago)</option>
                <option>MCO (Orlando)</option>
                <option> DEN (Denver)</option>
                <option>ATL (Atlanta)</option>
                <option>MIA (Miami)</option>
                <option>Guadalajara (GDL)</option>
                <option>Fort Lauderdale (FLL)</option>
                <option>Fort Lauderdale (FLL)</option>
                <option>Denver (DEN)</option>
                <option>Los Angeles Airport (LAX)</option>
                <option>Guadalajara (GDL)</option>
                <option>Puerto Vallarta (PVR)</option>
                <option>San Juan (SJU)</option>
                <option>NA</option>
                <option>Newark International Airport (EWR)</option>
                <option>Others</option>
                <option>Alajuela Office (SJO)</option>
                <option>Aguadilla (BQN)</option>
                <option>Bajio (BJX)</option>
                <option>Cancun International Airport (CUN)</option>
                <option>Heathrow Airport (LHR)</option>
                <option>Montego Bay (MBJ)</option>
                <option>Heathrow Airport (LHR)</option>
                <option>Houston (IAH)</option>
                <option>Guadalajara (GDL)</option>
                <option>Tampa (TPA)</option>
                <option>Punta Cana (PUJ)</option>
                <option>St. Maarten (SXM)</option>
                <option>Warsaw Chopin Airport (WAW)</option>
                <option>Zagreb Airport (ZAG)</option>
                <option>Zagreb City (ZGDTN)</option>
              </select>
            </Field>

            {/* HOW DID CUSTOMER KNOW ABOUT ROUTES */}
            <Field
              label="How did customer come to know about Routes"
              error={errors.sourceDetail}
            >
              <select
                name="sourceDetail"
                value={form.sourceDetail}
                onChange={handleChange}
                className="input select56"
              >
                <option value="">Select</option>
                <option>Facebook</option>
                <option>Google Ads</option>
                <option>Referred by a Friend</option>
                <option>Have made booking with us earlier</option>
                <option>Came to know through OTA Websites</option>
                <option>Through Routes Website</option>
                <option>NA</option>
              </select>
            </Field>

            {/* MORE FIELDS... */}
            <Field
              label="Existing Customer"
              error={errors.existingCustomer}
            >
              <select
                name="existingCustomer"
                value={form.existingCustomer}
                onChange={handleChange}
                className="input select56"
              >
                <option>Yes</option>
                <option>No</option>
                <option>NA</option>
              </select>
            </Field>

            <Field
              label="Reservation #"
              required
              error={errors.reservationNo}
            >
              <input
                name="reservationNo"
                value={form.reservationNo}
                onChange={handleChange}
                placeholder="e.g., R-12345"
                className="input"
              />
            </Field>

            <Field
              label="Verification Done"
              error={errors.verificationDone}
            >
              <select
                name="verificationDone"
                value={form.verificationDone}
                onChange={handleChange}
                className="input select56"
              >
                <option>Yes</option>
                <option>No</option>
                <option>NA</option>
              </select>
            </Field>

            <Field label="CALL CATEGORY" error={errors.callCategory}>
              <select
                name="callCategory"
                value={form.callCategory}
                onChange={handleChange}
                className="input select56"
              >
                <option value="">Select</option>
                <option>Additional Charges</option>
                <option>Refund</option>
                <option>Early Pick up/ Late Pick up</option>
                <option>Complaint</option>
                <option>Shuttle Service</option>
                <option>Shuttle Service Complaint</option>
                <option>Final Invoice</option>
                <option>Damage Report</option>
                <option>Status of Reservation</option>
                <option>Insurance Info</option>
                <option>Cancellation</option>
                <option>Modification</option>
                <option>Lost Belongings</option>
                <option>Additional Services</option>
                <option>Blank Call</option>
                <option>Early drop off/ Late drop off</option>
                <option>VM</option>
                <option>Garage Calling</option>
                <option>Lost the Key / Left the key in the Car</option>
                <option>NA</option>
                <option>After hours drop off</option>
                <option>Claims Related information</option>
                <option>Child Seat</option>
                <option>Toll pass/ Toll related</option>
                <option>Winter Tire Information</option>
                <option>Wrong Number</option>
                <option>Job Related</option>
                <option>Location Address/ Hours of operation</option>
                <option>New Reservation</option>
                <option>Extention</option>
                <option>Parking Space related Information</option>
                <option>Pre- Auth Information</option>
                <option>Underage Policy/ Fee</option>
                <option>General Query</option>
              </select>
            </Field>

            <Field label="SALE" error={errors.saleStatus}>
              <select
                name="saleStatus"
                value={form.saleStatus}
                onChange={handleChange}
                className="input select56"
              >
                <option value="">Select</option>
                <option>Sale Done</option>
                <option>Existing Customer</option>
                <option>VM</option>
                <option>Sale Via Chat</option>
                <option>NA</option>
              </select>
            </Field>

            <Field
              label="Is it Sale Enquiry"
              error={errors.isSaleEnquiry}
            >
              <select
                name="isSaleEnquiry"
                value={form.isSaleEnquiry}
                onChange={handleChange}
                className="input select56"
              >
                <option>Yes</option>
                <option>No</option>
                <option>NA</option>
              </select>
            </Field>

            {String(form.isSaleEnquiry).trim().toLowerCase() === "yes" && (
              <Field
                label="If SALE enquiry – why sale did not get converted?"
                error={errors.saleReason}
              >
                <select
                  name="saleReason"
                  value={form.saleReason}
                  onChange={handleChange}
                  className="input select56"
                >
                  <option>Prices are too High</option>
                  <option>General Inquiry</option>
                  <option>Customer Not Eligible to Rent</option>
                  <option>Vehicle Not Available</option>
                  <option>Snow/ Winter Tyre Information</option>
                  <option>Wrong Number</option>
                  <option>Underage Customer/ Underage Fee Information</option>
                  <option>Window Shopping - Customer will call back</option>
                  <option>Wanted to Buy Car</option>
                  <option>Customer wants to make booking online</option>
                  <option>Mandatory Requirements Information</option>
                  <option>Enquiry On Rental Price</option>
                  <option>Local Renter</option>
                  <option>Credit card/ Payments Information</option>
                  <option>Unhappy With Preauth/ Meileage Query</option>
                  <option>Existing Customer</option>
                </select>
              </Field>
            )}

            <Field label="Issue Resolved" error={errors.issueResolved}>
              <select
                name="issueResolved"
                value={form.issueResolved}
                onChange={handleChange}
                className="input select56"
              >
                <option>Yes</option>
                <option>No</option>
                <option>NA</option>
              </select>
            </Field>

            <Field
              label="If not resolved, reason"
              error={errors.issueNotResolvedReason}
            >
              <input
                name="issueNotResolvedReason"
                value={form.issueNotResolvedReason}
                onChange={handleChange}
                placeholder="Why was the issue not resolved?"
                className="input"
              />
            </Field>

            <Field label="Call Transferred" error={errors.callTransferred}>
              <select
                name="callTransferred"
                value={form.callTransferred}
                onChange={handleChange}
                className="input select56"
              >
                <option>Yes</option>
                <option>No</option>
                <option>NA</option>
              </select>
            </Field>

            <Field
              label="Call Transfer Reason (Why)"
              error={errors.callTransferReason}
            >
              <input
                name="callTransferReason"
                value={form.callTransferReason}
                onChange={handleChange}
                placeholder="Transfer reason"
                className="input"
              />
            </Field>

            <Field label="Pre Paid" error={errors.prePaid}>
              <select
                name="prePaid"
                value={form.prePaid}
                onChange={handleChange}
                className="input select56"
              >
                <option>Yes</option>
                <option>No</option>
                <option>NA</option>
              </select>
            </Field>

            <Field label="Post Paid" error={errors.postPaid}>
              <select
                name="postPaid"
                value={form.postPaid}
                onChange={handleChange}
                className="input select56"
              >
                <option>Yes</option>
                <option>No</option>
                <option>NA</option>
              </select>
            </Field>

            <Field
              label="Referred to website"
              error={errors.referredToWebsite}
            >
              <select
                name="referredToWebsite"
                value={form.referredToWebsite}
                onChange={handleChange}
                className="input select56"
              >
                <option>Yes</option>
                <option>No</option>
                <option>NA</option>
              </select>
            </Field>

            <Field label="Feedback" full error={errors.feedback}>
              <textarea
                name="feedback"
                value={form.feedback}
                onChange={handleChange}
                placeholder="Any notes or customer feedback…"
                className="input textarea"
              />
            </Field>
          </div>

          {/* STICKY SAVE BAR (only Save button) */}
          <div className="saveBar">
            <button type="submit" className="btnPrimary" disabled={saving}>
              {saving ? "Saving..." : "Save Interaction"}
            </button>
          </div>
        </form>
      </div>

      {/* Component-scoped CSS */}
      <style>{`
        /* PAGE PADDING REDUCED */
        .page {
          min-height: 100vh;
          display: flex;
          align-items: flex-start;
          justify-content: center;
          padding: 8px 4px; /* ↓ Reduced from 24px 12px */
          background: linear-gradient(135deg, #eef2ff 0%, #f0f9ff 40%, #ffffff 100%);
        }

        /* CARD PADDING REDUCED */
        .card {
          width: 100%;
          max-width: 1250px; /* ↑ Increased width to fit more fields */
          border-radius: 20px;
          border: 1px solid rgba(255,255,255,0.7);
          background: rgba(255,255,255,0.85);
          backdrop-filter: blur(10px);
          box-shadow: 0 14px 40px rgba(15,23,42,0.18);
        }

        .cardHeader {
          padding: 18px 20px 12px;
          background: linear-gradient(180deg, rgba(255,255,255,0.9), rgba(255,255,255,0.7));
          border-bottom: 1px solid rgba(15,23,42,0.04);
          text-align: center;
        }

        .badge {
          display:inline-block;
          font-size:11px;
          font-weight:700;
          color:#1d4ed8;
          background:#e0e7ff;
          padding:4px 10px;
          border-radius:999px;
        }

        .title {
          margin:8px 0 2px;
          font-size:22px;
          line-height:1.2;
          font-weight:800;
          letter-spacing:-0.02em;
          background:linear-gradient(90deg,#1d4ed8,#4338ca);
          -webkit-background-clip:text;
          background-clip:text;
          color:transparent;
        }

        .subtitle {
          margin:0;
          font-size:11px;
          color:#6b7280;
        }

        @media (min-width: 768px) {
          .title { font-size:26px; }
          .subtitle { font-size:12px; }
        }

        .form {
          padding: 10px 12px 6px; /* ↓ Reduced from 20px 20px */
        }
        @media (min-width: 768px) {
          .form {
            padding: 20px 20px 10px;
          }
        }

        .grid {
          display: grid;
          grid-template-columns: 1fr;
          gap: 14px 14px;
        }

        @media (min-width: 768px) {
          .form {
            padding: 12px 14px 8px; /* ↓ smaller desktop padding */
          }
        }

        @media (min-width: 1024px) {
          .grid {
            grid-template-columns: repeat(3, minmax(0, 1fr));
          }
        }

        .field {
          width: 100%;
        }

        .field.full {
          grid-column: 1 / -1;
        }

        .label {
          font-size:12px;
          font-weight:600;
          color:#374151;
          margin-bottom:4px;
          display:inline-block;
        }

        .req {
          color:#e11d48;
          margin-left:2px;
        }

        .opt {
          color:#9ca3af;
          font-weight:400;
          margin-left:4px;
          font-size:11px;
        }

        .input {
          width:100%;
          border:1px solid #e5e7eb;
          border-radius:12px;
          padding:10px 12px;
          font-size:13px;
          background:#ffffff;
          transition: box-shadow .18s, border-color .18s, transform .04s;
        }

        .input:focus {
          outline:none;
          border-color:#3b82f6;
          box-shadow:0 0 0 4px rgba(59,130,246,0.18);
        }

        .input-error {
          border-color:#ef4444 !important;
          box-shadow:0 0 0 4px rgba(239,68,68,0.18) !important;
        }

        .select56 {
          height:44px;
          padding:0 40px 0 12px;
          appearance:none;
          -webkit-appearance:none;
          background-image:
            linear-gradient(45deg, transparent 50%, #6b7280 50%),
            linear-gradient(135deg, #6b7280 50%, transparent 50%),
            linear-gradient(to right, #e5e7eb, #e5e7eb);
          background-position:
            right 14px center,
            right 9px center,
            right 2.3em center;
          background-size:6px 6px, 6px 6px, 1px 1.4em;
          background-repeat:no-repeat;
        }

        .textarea {
          min-height:90px;
          resize:vertical;
        }

       /* SAVE BAR SLIMMER */
        .saveBar {
          position: sticky;
          bottom: 0;
          padding: 6px 10px; /* ↓ Reduced from 8px 20px */
          background: rgba(255,255,255,0.96);
          border-top: 1px solid rgba(148,163,184,0.25);
          display: flex;
          justify-content: flex-end;
        }
        @media (min-width: 768px) {
          .saveBar {
            padding: 8px 20px 12px;
          }
        }

        .btnPrimary {
          border:0;
          cursor:pointer;
          font-weight:700;
          color:#ffffff;
          padding:10px 20px;
          border-radius:999px;
          background:linear-gradient(90deg,#2563eb,#4f46e5);
          box-shadow:0 10px 22px rgba(37,99,235,0.32);
          font-size:13px;
          transition:transform .06s, box-shadow .2s, filter .2s;
        }

        .btnPrimary:hover:not(:disabled) {
          box-shadow:0 12px 30px rgba(37,99,235,0.45);
          filter:brightness(1.04);
        }

        .btnPrimary:active:not(:disabled) {
          transform:translateY(1px);
          box-shadow:0 6px 16px rgba(37,99,235,0.38);
        }

        .btnPrimary:disabled {
          opacity:.65;
          cursor:not-allowed;
          box-shadow:none;
        }
      `}</style>
    </div>
  );
}
